import * as angular from "angular";
import { Dashboard } from "../models/dashboard.model";
import { DashboardWidget } from "../models/dashboard-widget.model";
import { DashboardService } from "../services/dashboard.service";
import { DashboardWidgetFilter } from "../models/dashboard-widget-filter.model";
import { CanvassStatus } from "../models/canvass-status.model";
import { CanvassStatusDataProvider } from "./canvass-status.dp";
import { PermissionCheck } from "../shared/permission";
import {
    DASHBOARD_DATA_TABLE_COLUMNS,
    DashboardWidgetTableColumnKey,
} from "../utils/constants/dashboard-widget.constants";
import { DashboardWidgetTableColumn } from "../models/dashboard-widget-table-column.model";

export class DashboardDataProvider {
    private readonly $q: any;
    private readonly isLeaderView: boolean;

    public dashboards: Dashboard[] = null;
    public userDashboards: Dashboard[] = null;
    public groupDashboards: Dashboard[] = null;
    public service: DashboardService;
    private canvassStatusDataProvider: CanvassStatusDataProvider;
    private permissionProvider: PermissionCheck;

    constructor(
        $q: any,
        DashboardService: DashboardService,
        CanvassStatusDataProvider: CanvassStatusDataProvider,
        PermissionCheck: PermissionCheck,
        __app_type: string
    ) {
        this.isLeaderView = __app_type == "LEADER";
        this.$q = $q;
        this.service = DashboardService;
        this.canvassStatusDataProvider = CanvassStatusDataProvider;
        this.permissionProvider = PermissionCheck;
    }

    dedupeWidgetColumns(widget: DashboardWidget): void {
        if (
            !widget.style ||
            !widget.style.tableOptions ||
            !widget.style.tableOptions.columns ||
            widget.style.tableOptions.columns.length === 0
        ) {
            return;
        }
        // duplicate columns have been persisted in the past, this cleans it up for the user
        const seen = new Map<string, any>();
        widget.style.tableOptions.columns.forEach((column: any): void => {
            const existing = seen.get(column.id);
            if (existing) {
                if (column.selected) {
                    existing.selected = true;
                }
            } else {
                seen.set(column.id, column);
            }
        });
        widget.style.tableOptions.columns = Array.from(seen.values());
    }

    migrateOldColumnsToNew(widget: DashboardWidget, tableColumns: any): any {
        let newCols = tableColumns[widget.datatype];

        this.dedupeWidgetColumns(widget);

        angular.forEach(newCols, (newCol) => {
            const existingColumn = widget.style.tableOptions.columns.filter(
                (column: any) => {
                    return column.id == newCol.id;
                }
            );
            if (existingColumn.length <= 0) {
                newCol.selected = true;
                widget.style.tableOptions.columns.push(newCol);
            }
        });
        //Check if old style of columns needs to be migrated to new way
        const selectedColumns = widget.style.tableOptions.columns.filter(
            (column: any) => {
                return column.selected;
            }
        );

        if (newCols)
            if (
                widget.type == "table" &&
                widget.filter.selected &&
                (!widget.style.tableOptions.columns ||
                    widget.style.tableOptions.columns.length <= 0 ||
                    selectedColumns.length <= 0)
            ) {
                return newCols;
            } else if (
                //If field tracking previously disabled, but now enabled, add GPS columns to the table
                widget.datatype == "users" &&
                widget.style &&
                widget.style.tableOptions &&
                widget.style.tableOptions.columns &&
                this.permissionProvider.hasPermission(
                    "CANVASSER_TRACKING_ALLOWED"
                )
            ) {
                let foundGPSCols = false;
                angular.forEach(widget.style.tableOptions.columns, (column) => {
                    if (column.id.includes("gps")) {
                        foundGPSCols = true;
                    }
                });

                if (!foundGPSCols) {
                    angular.forEach(newCols, (column) => {
                        if (column.id.includes("gps")) {
                            widget.style.tableOptions.columns.push(column);
                        }
                    });
                }
            }

        return null;
    }

    all() {
        const deferred = this.$q.defer();
        this.service.all().then(
            (response: any): void => {
                this.dashboards = [];
                let json = response["data"];

                for (let i = 0; i < json.data.length; i++) {
                    this.dashboards.push(new Dashboard(json.data[i]));
                }

                deferred.resolve(this.dashboards);
            },
            (error): void => {
                this.dashboards = [];
                deferred.reject(error);
            }
        );

        return deferred.promise;
    }

    user() {
        const deferred = this.$q.defer();
        this.service.user().then(
            (response: any): void => {
                this.userDashboards = [];
                let json = response["data"];
                for (let i = 0; i < json.data.length; i++) {
                    this.userDashboards.push(new Dashboard(json.data[i]));
                }
                deferred.resolve(this.userDashboards);
            },
            (error): void => {
                this.userDashboards = [];
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    group() {
        const deferred = this.$q.defer();
        this.service.group().then(
            (response: any): void => {
                this.groupDashboards = [];
                let json = response["data"];
                for (let i = 0; i < json.data.length; i++) {
                    this.groupDashboards.push(new Dashboard(json.data[i]));
                }
                deferred.resolve(this.groupDashboards);
            },
            (error): void => {
                this.groupDashboards = [];
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    widgetChangeRequiresReload(
        newValue: DashboardWidget,
        oldValue: DashboardWidget
    ): boolean {
        // No reload needed if no old value or same reference
        if (!oldValue || newValue === oldValue) {
            return false;
        }

        // Name-only changes don't require reload
        if (newValue.name !== oldValue.name) {
            return false;
        }

        // Prepare filters for comparison (excluding volatile date fields)
        const oldFilter: DashboardWidgetFilter = angular.copy(oldValue.filter);
        const newFilter: DashboardWidgetFilter = angular.copy(newValue.filter);
        delete oldFilter.dateRange.dateStart;
        delete oldFilter.dateRange.dateEnd;
        delete newFilter.dateRange.dateStart;
        delete newFilter.dateRange.dateEnd;

        // Define change conditions
        const datatypeChanged: boolean =
            newValue.datatype !== oldValue.datatype;
        const filterUnchanged: boolean =
            JSON.stringify(oldFilter) === JSON.stringify(newFilter);
        const chartOptionsUnchanged: boolean =
            JSON.stringify(newValue.style.chartOptions) ===
            JSON.stringify(oldValue.style.chartOptions);
        const groupbyChanged: boolean =
            JSON.stringify(newValue.groupby) !==
            JSON.stringify(oldValue.groupby);

        const tableOptionsUnchanged: boolean =
            JSON.stringify(newValue.style.tableOptions) ===
            JSON.stringify(oldValue.style.tableOptions);
        const columnsChanged: boolean =
            JSON.stringify(newValue.style.tableOptions.columns) !==
            JSON.stringify(oldValue.style.tableOptions.columns);
        const rowsChanged: boolean =
            JSON.stringify(newValue.style.tableOptions.rows) !==
            JSON.stringify(oldValue.style.tableOptions.rows);
        const legendUnchanged: boolean =
            JSON.stringify(newValue.style.legend) ===
            JSON.stringify(oldValue.style.legend);
        const fontSizeUnchanged: boolean =
            JSON.stringify(newValue.style.fontSize) ===
            JSON.stringify(oldValue.style.fontSize);

        const tableRelatedCondition =
            (tableOptionsUnchanged || columnsChanged || rowsChanged) &&
            legendUnchanged &&
            fontSizeUnchanged;

        return (
            datatypeChanged ||
            filterUnchanged ||
            chartOptionsUnchanged ||
            groupbyChanged ||
            tableRelatedCondition
        );
    }

    saveGrid(dashboard: any) {
        const deferred = this.$q.defer();

        let formattedItems = [];
        angular.forEach(dashboard.widgets, (widget) => {
            let item = {
                id: widget.id,
                dashboardid: dashboard.id,
                width: widget.w ?? 0,
                height: widget.h ?? 0,
                x_pos: widget.x,
                y_pos: widget.y,
            };
            formattedItems.push(item);
        });

        this.service.saveGrid(dashboard.id, { items: formattedItems }).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    getDataTableColumns(): any {
        const deferred: any = this.$q.defer();
        const tableColumns: Record<
            DashboardWidgetTableColumnKey,
            DashboardWidgetTableColumn[]
        > = angular.copy(DASHBOARD_DATA_TABLE_COLUMNS);

        this.permissionProvider.getFeaturePermissions().then(() => {
            this.canvassStatusDataProvider
                .walkCanvassStatuses()
                .then((statuses: CanvassStatus[]): void => {
                    angular.forEach(statuses, (status: CanvassStatus): void => {
                        angular.forEach(
                            tableColumns,
                            (columns: DashboardWidgetTableColumn[]): void => {
                                columns.push({
                                    id: "interaction_status_" + status.id,
                                    name: status.name + " Interactions",
                                    tooltip: null,
                                    selected: true,
                                });
                            }
                        );
                    });
                    if (this.permissionProvider.hasPermission("GO_APP")) {
                        angular.forEach(
                            tableColumns,
                            (columns: DashboardWidgetTableColumn[]): void => {
                                columns.push({
                                    id: "interaction_status_" + 7,
                                    name: "Answered Interactions (Go App)",
                                    tooltip: null,
                                    selected: true,
                                });
                                columns.push({
                                    id: "interaction_status_" + 8,
                                    name: "Not Interested Interactions (Go App)",
                                    tooltip: null,
                                    selected: true,
                                });
                            }
                        );
                    }
                    if (
                        !this.isLeaderView &&
                        this.permissionProvider.hasPermission(
                            "CANVASSER_TRACKING_ALLOWED"
                        )
                    ) {
                        tableColumns.users.push({
                            id: "green_gps",
                            name: "Field Tracking GPS (Green)",
                            tooltip:
                                "Interactions that were recorded within 100m of the house location",
                            selected: true,
                        });
                        tableColumns.users.push({
                            id: "orange_gps",
                            name: "Field Tracking GPS (Orange)",
                            tooltip:
                                "Interactions that were recorded between 100m and 300m from the house location",
                            selected: true,
                        });
                        tableColumns.users.push({
                            id: "red_gps",
                            name: "Field Tracking GPS (Red)",
                            tooltip:
                                "Interactions that were recorded between 100m and 300m from the house location",
                            selected: true,
                        });
                        tableColumns.users.push({
                            id: "grey_gps",
                            name: "Field Tracking GPS (Not Captured)",
                            tooltip:
                                "Interactions that were recorded with either inaccurate or missing GPS data",
                            selected: true,
                        });
                    }
                    deferred.resolve(tableColumns);
                });
        });

        return deferred.promise;
    }

    create(dashboard: Dashboard) {
        const deferred = this.$q.defer();
        this.service.create(dashboard).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    let dashboard: Dashboard = new Dashboard(json.data);
                    if (this.dashboards != null) {
                        this.dashboards.push(dashboard);
                    }
                    deferred.resolve(dashboard);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    update(dashboard: any) {
        const deferred = this.$q.defer();
        this.service.update(dashboard.id, dashboard).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(dashboard);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    duplicate(dashboard: any) {
        const deferred = this.$q.defer();
        this.service.duplicate(dashboard.id, dashboard).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    let returnedData = angular.copy(dashboard);
                    returnedData.id = json.data.id;
                    deferred.resolve(returnedData);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    delete(dashboard: any) {
        const deferred = this.$q.defer();
        this.service.delete(dashboard.id).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(dashboard);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    createWidget(widget: DashboardWidget) {
        const deferred = this.$q.defer();
        this.service.createWidget(widget).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    let widget: DashboardWidget = new DashboardWidget(
                        json.data
                    );
                    deferred.resolve(widget);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    updateWidget(widget: DashboardWidget) {
        const deferred = this.$q.defer();
        this.service.updateWidget(widget.id, widget).then(
            (response: any): void => {
                let json = response["data"];
                if (json.success) {
                    deferred.resolve(widget);
                } else {
                    deferred.reject(json);
                }
            },
            (error: any): void => deferred.reject(error)
        );
        return deferred.promise;
    }

    deleteWidget(widget: DashboardWidget) {
        widget.status = "Deleted";
        return this.updateWidget(widget);
    }

    clearAll() {
        this.dashboards = null;
    }
}

angular
    .module("vcDataProvider")
    .service("DashboardDataProvider", DashboardDataProvider);
